// src/map/overlay.js
// Extent-based, debounced overlay painter for ArcGIS JS MapView.
// Fetches overlay config from /api/config (arcgis.mapOverlays.distributionLines, arcgis.mapOverlays.serviceArea).
// No overrides are supported—config is the single source of truth.

import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import Graphic from "@arcgis/core/Graphic";
import * as geometryJsonUtils from "@arcgis/core/geometry/support/jsonUtils";
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";

// Load /api/config once (cached). Expects: { config: { mapOverlays: { distributionLines: {...}, serviceArea: {...} } } }
let __allOverlayConfigs = null;
async function getAllOverlayConfigs() {
  if (__allOverlayConfigs) return __allOverlayConfigs;
  const resp = await fetch("/api/config", { headers: { Accept: "application/json" }, credentials: "same-origin" });
  if (!resp.ok) throw new Error(`GET /api/config failed: ${resp.status}`);
  const json = await resp.json();
  
  // Get overlay configs from unified structure
  const mapOverlays = json?.config?.mapOverlays;
  if (mapOverlays && typeof mapOverlays === "object") {
    __allOverlayConfigs = mapOverlays;
    return __allOverlayConfigs;
  }
  
  throw new Error("No overlay configuration found in /api/config");
}

function symbolFromConfig(geometryType, styleConfig) {
  // Use custom style if provided, otherwise fall back to defaults
  if (styleConfig && styleConfig.type === "fill") {
    // Fill style (for polygons like service boundary)
    return {
      type: "simple-fill",
      color: styleConfig.fillColor || [0, 122, 255, 0.15],
      outline: {
        color: styleConfig.outlineColor || [0, 122, 255, 0.8],
        width: Number(styleConfig.outlineWidth) || 2
      }
    };
  } else if (styleConfig && styleConfig.type === "line") {
    // Line style (for infrastructure)
    return {
      type: "simple-line",
      width: Number(styleConfig.width) || 3,
      color: styleConfig.color || [0, 120, 255, 1]
    };
  }
  
  // Default symbols based on geometry type (backward compatible)
  const w = Number(styleConfig?.width || styleConfig?.strokeWidth || 3);
  switch (geometryType) {
    case "point":
    case "multipoint":
      return { type: "simple-marker", style: "circle", size: 7, color: [0,122,255,0.95], outline: { color: [255,255,255,0.9], width: 1 } };
    case "polyline":
      return { type: "simple-line", width: w, color: [0,122,255,1] };
    case "polygon":
    case "extent":
      return { type: "simple-fill", color: [0,122,255,0.18], outline: { color: [0,122,255,1], width: Math.max(1, Math.round(w * 0.6)) } };
    default:
      return { type: "simple-line", width: w, color: [0,122,255,1] };
  }
}

function replaceLayerAtBottom(view, layer) {
  const existing = view.map.findLayerById(layer.id);
  if (existing) view.map.remove(existing);
  try { view.map.add(layer, 0); } catch { view.map.add(layer); }
}

function extentOfGraphics(graphics) {
  let ext = null;
  for (let i = 0; i < graphics.length; i++) {
    const ge = graphics[i]?.geometry?.extent || null;
    if (!ge) continue;
    ext = ext ? ext.union(ge) : ge;
  }
  return ext;
}

// Compute maxAllowableOffset from current zoom in map units for ~maoPx pixels
/*
 * maxAllowableOffset (MAO) controls geometry simplification during ArcGIS queries
 * Zoom out = higher MAO = more simplified geometry (faster, less detail)
 * Zoom in = lower MAO = more detailed geometry (slower, sharper lines)
 * We calculate based on pixels to keep visual quality consistent across zoom levels
 */
function computeMao(view, maoPx) {
  const ext = view.extent;
  const px = Number(maoPx);
  const w = view.width;
  const metersPerPixel = ext.width / w;              // How many map units per screen pixel
  return Math.max(0, metersPerPixel * px);            // Scale MAO by zoom level
}

// Pad extent slightly to catch edge-crossers
function paddedExtent(ext, pct) {
  const padX = ext.width * pct;
  const padY = ext.height * pct;
  return {
    xmin: ext.xmin - padX,
    ymin: ext.ymin - padY,
    xmax: ext.xmax + padX,
    ymax: ext.ymax + padY,
    wkid: ext.spatialReference?.wkid || 102100,
  };
}

/**
 * Attach a single overlay type
 * @param {import("@arcgis/core/views/MapView").default} view
 * @param {string} overlayType - e.g., "distributionLines", "serviceArea"
 * @param {object} opts - client configuration
 */
async function attachSingleOverlay(view, overlayType, opts) {
  const endpoint = `/api/overlays/${overlayType}`;
  const layerIdPrefix = `${overlayType}-`;
  
  let abortCtrl = null;
  let firstDraw = true;
  let timer = null;

  const drawForExtent = async () => {
    const ext = view.extent;
    if (!ext) return;

    // Check scale thresholds (confusing but necessary: minScale = zoom OUT limit, maxScale = zoom IN limit)
    // minScale: show when zoomed IN (scale < minScale)
    // maxScale: show when zoomed OUT (scale > maxScale)
    if (opts.minScale > 0 && view.scale > opts.minScale) {
      // Zoomed out too far, hide layers
      const existing = view.map.layers.items.filter(l => l.id && l.id.startsWith(layerIdPrefix));
      existing.forEach(l => l.removeAll());
      return;
    }
    if (opts.maxScale > 0 && view.scale < opts.maxScale) {
      // Zoomed in too far, hide layers
      const existing = view.map.layers.items.filter(l => l.id && l.id.startsWith(layerIdPrefix));
      existing.forEach(l => l.removeAll());
      return;
    }

    const env = paddedExtent(ext, Number(opts.extentPadPct || 0.12));
    const mao = computeMao(view, Number(opts.maoPx || 8));

    // Abort in-flight
    if (abortCtrl) { try { abortCtrl.abort(); } catch {} }
    abortCtrl = new AbortController();

    // Fetch
    const url = new URL(endpoint, window.location.origin);
    url.searchParams.set("xmin", env.xmin);
    url.searchParams.set("ymin", env.ymin);
    url.searchParams.set("xmax", env.xmax);
    url.searchParams.set("ymax", env.ymax);
    url.searchParams.set("wkid", env.wkid);
    url.searchParams.set("mao", String(Math.round(mao)));
    url.searchParams.set("max", String(Number(opts.maxPerExtent || 6000)));

    try {
      const resp = await fetch(url.toString(), {
        method: "GET",
        headers: { Accept: "application/json" },
        credentials: "same-origin",
        signal: abortCtrl.signal
      });
      if (!resp.ok) throw new Error(`Overlay fetch failed: HTTP ${resp.status}`);
      const payload = await resp.json();

      if (!payload || !Array.isArray(payload.layers) || !payload.layers.length) {
        return;
      }

      // Draw
      payload.layers.forEach((layerInfo) => {
        const feats = Array.isArray(layerInfo.features) ? layerInfo.features : [];
        const layerSR = layerInfo?.spatialReference || null;
        const layerWkid = layerSR && (layerSR.wkid || layerSR.latestWkid) ? Number(layerSR.wkid || layerSR.latestWkid) : null;

        // Build complete graphics array FIRST (before creating layer)
        // This prevents WebGL from rendering partial geometry (triangle flash)
        const graphics = [];
        for (let i = 0; i < feats.length; i++) {
          const f = feats[i];
          const raw = f && f.geometry ? f.geometry : null;
          if (!raw) continue;
          const geoJson = !raw.spatialReference && layerWkid ? { ...raw, spatialReference: { wkid: layerWkid } } : raw;
          const geom = geometryJsonUtils.fromJSON(geoJson);
          if (!geom) continue;
          
          // Polygon overlay will show triangle flash if rendered too fast, wait for more polygon points to render
          if (geom.type === 'polygon' && geom.rings) {
            const vertexCount = geom.rings.reduce((sum, ring) => sum + ring.length, 0);
            
            //EOFIX - 20 vertex minimum is arbitrary, might need tuning per dataset
            if (vertexCount < 20) {                                               // Skip simplified polygons
              console.warn(`[overlay:${overlayType}] Skipping incomplete polygon with only ${vertexCount} vertices`);
              continue;  // Don't add - WebGL would render partial triangles
            }
          }
          
          const symbol = symbolFromConfig(geom.type, opts.style);
          graphics.push(new Graphic({ geometry: geom, attributes: f.attributes || {}, symbol }));
        }

        // NOW create layer with complete graphics array
        const gl = new GraphicsLayer({ 
          id: `${layerIdPrefix}${layerInfo.id}`, 
          title: layerInfo.name || `${overlayType} ${layerInfo.id}`,
          graphics: graphics  // All graphics added at construction
        });
        
        replaceLayerAtBottom(view, gl);

        if (firstDraw && opts.zoomTo && graphics.length) {
          const union = extentOfGraphics(graphics);
          if (union) view.goTo(union.expand(1.1), { duration: 400 }).catch(() => {});
        }
      });

      firstDraw = false;
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error(`[overlay:${overlayType}]`, err);
      }
    }
  };

  // Initial draw
  await drawForExtent();

  // Debounced redraw on extent change
  const watchKey = `__overlayWatch_${overlayType}`;
  try { view[watchKey]?.remove?.(); } catch {}
  view[watchKey] = reactiveUtils.watch(
    () => view.extent,
    () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(drawForExtent, Number(opts.debounceMs || 600));
    }
  );
}

/**
 * Attach all enabled overlays from config
 * @param {import("@arcgis/core/views/MapView").default} view
 */
export async function attachOverlays(view) {
  await view.when();
  const allConfigs = await getAllOverlayConfigs();
  
  for (const [overlayType, config] of Object.entries(allConfigs)) {
    if (config.enabled !== false && config.client) {
      console.log(`[overlay] Attaching ${overlayType}`);
      await attachSingleOverlay(view, overlayType, config.client);
    }
  }
}

/**
 * Legacy function for backward compatibility
 * @param {import("@arcgis/core/views/MapView").default} view
 */
export async function attachOverlay(view) {
  console.warn("[overlay] attachOverlay() is deprecated, use attachOverlays() instead");
  await attachOverlays(view);
}
